export default {
  article: {
    metaTitle: 'Basetao Spreadsheet Guide 2026: So organisieren Sie Ihren Einkauf',
    metaDescription:
      'Erfahren Sie, wie Sie eine Basetao Spreadsheet erstellen und nutzen, um Produktlinks, Verkäufer, QC-Fotos und Versanddetails an einem Ort zu organisieren.',
    breadcrumbHome: 'Startseite',
    breadcrumbCurrent: 'Basetao Spreadsheet Guide',
    title: 'Der vollständige Basetao Spreadsheet Guide (2026)',
    subtitle:
      'Ein praktischer Leitfaden zum Erstellen und Nutzen einer Basetao Spreadsheet, um Produktlinks, Verkäufer, QC-Fotos und Versanddetails zu organisieren.',
    authorLabel: 'Von',
    author: 'Basetao Spreadsheet Team',
    updatedLabel: 'Zuletzt aktualisiert',
    updated: 'August 2026',
    readingTime: '8 Min. Lesezeit',
    tocLabel: 'Auf dieser Seite',
    intro: {
      heading: 'Einleitung',
      p1: 'Der Einkauf über einen chinesischen Einkaufsagenten wie Basetao öffnet die Tür zu einer riesigen Produktpalette — von Sneakers und Kleidung bis hin zu Accessoires und Alltagsgegenständen. Doch sobald sich Ihr Warenkorb füllt, kann es schnell überwältigend werden, jeden Artikel, Verkäufer, Preis und jedes QC-Foto im Blick zu behalten.',
      p2: 'Genau hier kommt eine Basetao Spreadsheet ins Spiel. Sie ist eine einfache, organisierte Möglichkeit, alles aufzuzeichnen, was Sie kaufen, damit Sie immer wissen, was Sie bestellt haben, wo es ist und wie viel es gekostet hat. Dieser Leitfaden zeigt Ihnen alles, was Sie brauchen, um eine solche Tabelle effektiv zu erstellen und zu nutzen.',
    },
    whatIsBasetao: {
      heading: 'Was ist Basetao?',
      p1: 'Basetao ist ein Einkaufsagent, der internationalen Käufern hilft, Artikel von chinesischen Online-Plattformen wie Taobao, 1688 und Weidian zu kaufen. Er fungiert als Mittelschicht: Sie fügen einen Produktlink ein, der Agent kauft den Artikel für Sie, lagert ihn in seinem Lager und versendet ihn dann in Ihr Land. Unterwegs können Sie QC-Fotos (Qualitätskontrolle) anfordern, um zu bestätigen, dass der Artikel korrekt aussieht, bevor er versendet wird.',
    },
    whatIsSpreadsheet: {
      heading: 'Was ist eine Basetao Spreadsheet?',
      p1: 'Eine Basetao Spreadsheet ist ein geteiltes Dokument — meist in Google Sheets oder Excel erstellt — das jedes Produkt auflistet, das Sie interessiert oder das Sie über den Agenten bestellt haben. Sie erfasst in der Regel den Produktlink, den Verkäufer, den Preis, die Größe oder Farbe, den QC-Status und den Versandfortschritt.',
      p2: 'Stellen Sie sie sich als Ihre persönliche Schaltzentrale für einen Basetao-Einkauf vor. Statt mit Dutzenden von Browser-Tabs und Chat-Nachrichten zu jonglieren, behalten Sie alles an einem Ort, den Sie auf einen Blick aktualisieren und überprüfen können.',
    },
    why: {
      heading: 'Warum eine Basetao Spreadsheet nutzen?',
      p1: 'Eine gut gepflegte Tabelle spart Zeit und verhindert kostspielige Fehler. Hier sind die wichtigsten Vorteile:',
      items: [
        'Viele Artikel gleichzeitig im Blick behalten — besonders nützlich für große Einkäufe.',
        'Preise und Verkäufer vergleichen, bevor Sie sich für einen Kauf entscheiden.',
        'QC-Ergebnisse festhalten, um schnell zu entscheiden, ob Sie einen Artikel behalten oder zurückgeben.',
        'Lager- und Versandstatus überwachen, ohne Chat-Verläufe durchsuchen zu müssen.',
        'Eine realistische Gesamtkostenberechnung inklusive Agentengebühren und Versand erstellen.',
      ],
    },
    whatToInclude: {
      heading: 'Was sollte in Ihre Tabelle?',
      p1: 'Die genauen Spalten hängen von Ihrer Einkaufsweise ab, aber diese Felder sind für fast alle nützlich:',
      items: [
        'Produktlink — die ursprüngliche URL, um die Anzeige jederzeit wieder zu öffnen.',
        'Verkäufername — um die Qualität zwischen verschiedenen Shops zu vergleichen.',
        'Preis — in lokaler Währung und in Ihrer eigenen Währung.',
        'Größe, Farbe und Menge — um die falsche Variante zu vermeiden.',
        'QC-Status und Fotolink — um zu verfolgen, welche Artikel die Prüfung bestanden haben.',
        'Lagerstatus — angekommen, eingelagert oder versandbereit.',
        'Versandmethode und Sendungsnummer — für die endgültige Lieferung.',
      ],
    },
    structure: {
      heading: 'Wie strukturieren Sie Ihre Tabelle?',
      p1: 'Ein klares Spaltenlayout macht Ihre Tabelle leicht zu überblicken und zu filtern. Eine praktische Ausgangsstruktur sieht so aus:',
      columns: [
        { col: 'Produktname', desc: 'Ein kurzer, klarer Name, um Artikel schnell zu finden.' },
        { col: 'Produktlink', desc: 'Die vollständige URL, um die Anzeige bei Bedarf wieder zu öffnen.' },
        { col: 'Verkäufer', desc: 'Der Shopname, nützlich zum Vergleichen der Qualität.' },
        { col: 'Preis', desc: 'Lokaler Preis und umgerechneter Preis in Ihrer Währung.' },
        { col: 'Größe / Farbe', desc: 'Die genaue Variante, die Sie kaufen möchten.' },
        { col: 'QC', desc: 'Ein schnelles Ja/Nein oder ein Link zu den QC-Fotos.' },
        { col: 'Status', desc: 'Bestellt, im Lager oder versendet.' },
      ],
    },
    tips: {
      heading: 'Tipps, um organisiert zu bleiben',
      items: [
        'Verwenden Sie eine Zeile pro Artikel, damit Sie leicht filtern und sortieren können.',
        'Kennzeichnen Sie Zeilen farblich nach Status, um den Fortschritt auf einen Blick zu sehen.',
        'Behalten Sie den ursprünglichen Produktlink — Anzeigen können sich ändern oder verschwinden.',
        'Aktualisieren Sie die Tabelle direkt nach jeder Aktion, statt sich auf Ihr Gedächtnis zu verlassen.',
        'Sichern Sie die Tabelle oder teilen Sie sie mit sich selbst in der Cloud.',
      ],
    },
    mistakes: {
      heading: 'Häufige Fehler, die Sie vermeiden sollten',
      items: [
        'Das QC überspringen und Probleme erst nach dem Versand entdecken.',
        'Die genaue Größe oder Farbe vergessen, was zu falschen Bestellungen führt.',
        'Versandkosten nicht nachverfolgen, was die Endsumme zur Überraschung macht.',
        'Unklare Produktnamen verwenden, die später nicht mehr auffindbar sind.',
        'Die Tabelle veralten lassen, sodass sie die Realität nicht mehr widerspiegelt.',
      ],
    },
    faq: {
      heading: 'Häufig gestellte Fragen',
      items: [
        {
          q: 'Ist eine Basetao Spreadsheet dasselbe wie die offizielle Basetao-Website?',
          a: 'Nein. Es ist ein unabhängiges Organisationstool. Es ist weder mit der offiziellen Basetao-Website verbunden noch ein Teil davon, sofern nicht ausdrücklich anders angegeben.',
        },
        {
          q: 'Brauche ich eine Tabelle für eine kleine Bestellung?',
          a: 'Nicht unbedingt, aber selbst wenige Artikel lassen sich leichter verwalten, wenn Links, Preise und QC-Status alle an einem Ort sind.',
        },
        {
          q: 'Welches Tool sollte ich zum Erstellen der Tabelle verwenden?',
          a: 'Google Sheets und Excel sind beide beliebt. Google Sheets ist von jedem Gerät aus leicht zugänglich und einfach zu teilen.',
        },
        {
          q: 'Was soll ich tun, wenn eine Produktanzeige verschwindet?',
          a: 'Halten Sie den Verkäufernamen und eine klare Beschreibung in Ihrer Tabelle fest und wenden Sie sich mit dem ursprünglichen Link an den Agenten, falls Sie ihn noch haben.',
        },
        {
          q: 'Verkauft diese Website Produkte direkt?',
          a: 'Nein. Diese Website ist eine Informationsressource. Produktkategorien verlinken auf die Hauptwebsite, wo Sie durchsuchen und kaufen können.',
        },
      ],
    },
    cta: {
      title: 'Bereit, Ihren nächsten Basetao-Einkauf zu organisieren?',
      desc: 'Entdecken Sie die ausgewählten Produktkategorien oder besuchen Sie die Hauptwebsite, um den gesamten Katalog zu durchsuchen.',
    },
  },
};